import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Bike,
  Briefcase,
  Building2,
  Car,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Star,
  Truck,
  UserRound,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import GoogleReviews from '../components/GoogleReviews';
import { buildPortalTrackingUrl, PORTAL_LINKS } from '../constants/site';

const FINANCE_OPTIONS = [
  {
    key: 'car',
    label: 'Car Finance',
    icon: Car,
    route: '/car-loans',
    portalBaseUrl: PORTAL_LINKS.CAR,
    portalProduct: 'car',
    ctaLabel: 'Get my car loan quote',
  },
  {
    key: 'personal',
    label: 'Personal Loans',
    icon: UserRound,
    route: '/personal-loans',
    portalBaseUrl: PORTAL_LINKS.PERSONAL,
    portalProduct: 'personal',
    ctaLabel: 'Get my personal loan quote',
  },
  {
    key: 'leisure',
    label: 'Leisure Finance',
    icon: Bike,
    route: '/leisure-finance',
    portalBaseUrl: PORTAL_LINKS.LEISURE,
    portalProduct: 'leisure',
    ctaLabel: 'Get my leisure finance quote',
  },
  {
    key: 'commercial',
    label: 'Commercial / Asset',
    icon: Truck,
    route: '/commercial-asset-finance',
    portalBaseUrl: PORTAL_LINKS.COMMERCIAL,
    portalProduct: 'commercial',
    ctaLabel: 'Get my commercial finance quote',
  },
  {
    key: 'business',
    label: 'Business Loans',
    icon: Briefcase,
    route: '/business-loans',
    portalBaseUrl: PORTAL_LINKS.BUSINESS,
    portalProduct: 'business',
    ctaLabel: 'Get my business loan quote',
  },
];

const LENDER_LOGOS = [
  {
    name: 'Wisr',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/mksf5rqn_31a64953-0cc6-4aff-af1f-3a635112b750_wisr.webp',
  },
  {
    name: 'Dynamoney',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/ljr0budn_65d551783a605798c18bfe77_logo1.webp',
  },
  {
    name: 'Selfco',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/tj1zut35_0246dae8-d87a-4e64-9d46-5de79d020846_selfco.webp',
  },
  {
    name: 'Angle Finance',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/jtlh0utk_8843aef8-9c35-4d75-81b1-0edfe3793ee1_cropped%2BangleFinance.webp',
  },
  {
    name: 'Plenti',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/4iykkvzl_8031644c-393b-41ac-87ce-9dac40125ac1_Plenti_StandardVersion_Navy.webp',
  },
  {
    name: 'Azora',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/csbe1f2o_aRq7L7pReVYa4hpp_AzoraLogo2025.webp',
  },
  {
    name: 'CarStart',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/79gqi5lz_aXBoDgIvOtkhBwsj_CarStartLogo.webp',
  },
  {
    name: 'MoneyPlace',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/3bzkjb8b_c0f21ce4-ac3a-4439-85ef-304cc9dba9d5_moneyplace-logo.webp',
  },
  {
    name: 'Shift',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/xxas16iy_dba1b1cb-1fc8-49c9-a49e-32ff8b459a32_download.webp',
  },
  {
    name: 'Metro',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/fv84aimc_f755d3b3-04f2-4800-ad79-62d603fbee52_metro_logo_screen_landscape_blue_rgb%2B%281%29.webp',
  },
  {
    name: 'Liberty',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/43rb1ulj_liberty.svg',
  },
  {
    name: 'NOW Finance',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/fn1zeblt_NOWFinance-Logo.svg',
  },
  {
    name: 'Prospa',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/67d5z7qy_prospa.svg',
  },
  {
    name: 'Finance One',
    image: 'https://customer-assets.emergentagent.com/job_70c4092b-df23-4055-8885-7b42704aa847/artifacts/vfvsw75z_ZjB7IN3JpQ5PTQgg_LOGO-02.webp',
  },
];

const HOW_IT_WORKS = [
  {
    title: 'Tell us what you need',
    copy: 'Share your purpose and rough numbers in under a minute.',
  },
  {
    title: 'We compare 40+ lenders',
    copy: 'Your profile is matched to lender fit and likely approval pathways.',
  },
  {
    title: 'Get approved faster',
    copy: 'We package and progress your file so you are not chasing updates alone.',
  },
];
const GUIDE_CARDS = [
  {
    title: 'Equipment finance guide for Australian businesses',
    description: 'Understand structures, repayments, and when each product fits.',
    path: '/equipment-finance-guide-australia',
  },
  {
    title: 'Explore our full learning hub',
    description: 'Borrowing tips, lender insights and practical education content.',
    path: '/learn',
  },
];

const PATHWAY_IMAGES = {
  personal:
    'https://images.unsplash.com/photo-1725975810083-eebd7e8f965d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB5b3VuZyUyMGF1c3RyYWxpYW4lMjBmYW1pbHklMjBjYXIlMjBoYXBweXxlbnwwfHx8fDE3Nzc2MjU5NTd8MA&ixlib=rb-4.1.0&q=85',
  business:
    'https://images.unsplash.com/photo-1774291635042-e1854c834f88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG93bmVyJTIwdHJhZGllJTIwY29uc3RydWN0aW9uJTIwbG9naXN0aWNzfGVufDB8fHx8MTc3NzYyNTk1N3ww&ixlib=rb-4.1.0&q=85',
};

const HOME_FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How fast can I get a business loan in Australia?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Through Flash Funding, many business loan applications receive a decision within 24 hours, depending on the applicant, the lender's credit criteria and the type of loan. We package your file correctly the first time, reducing back-and-forth with lenders and speeding up approval."
      }
    },
    {
      "@type": "Question",
      "name": "What is commercial asset finance?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Commercial asset finance lets Australian businesses fund the purchase of equipment, vehicles, and machinery without paying the full cost upfront. Common structures include chattel mortgage, finance lease, and operating lease."
      }
    },
    {
      "@type": "Question",
      "name": "Will applying affect my credit score?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No. Flash Funding's initial assessment does not impact your credit score. We assess your situation first and only proceed to a formal credit application once we've identified the right lender for you."
      }
    },
    {
      "@type": "Question",
      "name": "How many lenders does Flash Funding work with?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Flash Funding has a panel of 40+ lenders across business finance, asset finance, and personal lending. This means we can match your application to the lender most likely to approve it on the best terms."
      }
    },
    {
      "@type": "Question",
      "name": "Can I get finance if I've been declined before?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Flash Funding works with a broad lender panel including lenders who specialise in non-prime and complex credit situations. Being declined by one lender does not mean you won't qualify elsewhere."
      }
    },
    {
      "@type": "Question",
      "name": "Is Flash Funding an Australian company?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Flash Funding is a trading name of Flash Funding Group Pty Ltd ABN 17 698 240 578. Flash Funding Group Pty Ltd is an Authorised Credit Representative 578970 of Australian Credit Licence 414426, AFAS Group Pty Ltd ABN 12 134 138 686."
      }
    }
  ]
};

export default function Home() {
  const [selectedPurpose, setSelectedPurpose] = useState('car');
  const [amount, setAmount] = useState(120000);
  const [termYears, setTermYears] = useState(5);
  const [interestRate, setInterestRate] = useState(8.4);
  const [balloonPercent, setBalloonPercent] = useState(20);
  const [mobileLenderSlideIndex, setMobileLenderSlideIndex] = useState(0);
  const [mobileLenderTouchStartX, setMobileLenderTouchStartX] = useState(null);
  const [isMobileLenderCarousel, setIsMobileLenderCarousel] = useState(false);

  const monthlyRepayment = useMemo(() => {
    const monthlyRate = (interestRate / 100) / 12;
    const n = termYears * 12;

    if (n <= 0) return 0;

    const balloonAmount = amount * (balloonPercent / 100);

    let payment = 0;
    if (monthlyRate === 0) {
      payment = (amount - balloonAmount) / n;
    } else {
      payment = ((amount - (balloonAmount / Math.pow(1 + monthlyRate, n))) * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));
    }

    return Number.isFinite(payment) ? payment : 0;
  }, [amount, termYears, interestRate, balloonPercent]);

  const selectedOption = useMemo(
    () => FINANCE_OPTIONS.find((option) => option.key === selectedPurpose) || FINANCE_OPTIONS[0],
    [selectedPurpose]
  );

  const selectedFinderCta = useMemo(() => {
    if (!selectedOption.portalBaseUrl || !selectedOption.portalProduct) {
      return {
        href: selectedOption.route,
        label: selectedOption.ctaLabel,
        external: false,
      };
    }

    return {
      href: buildPortalTrackingUrl(selectedOption.portalBaseUrl, selectedOption.portalProduct, 'finance_finder'),
      label: selectedOption.ctaLabel,
      external: true,
    };
  }, [selectedOption]);

  const lenderPanelItems = useMemo(
    () => [
      ...LENDER_LOGOS.map((logo) => ({ ...logo, type: 'logo' })),
      { name: 'And Many More', type: 'placeholder' },
    ],
    []
  );

  const mobileLenderSlides = useMemo(() => {
    const chunkSize = 2;
    const slides = [];
    for (let i = 0; i < lenderPanelItems.length; i += chunkSize) {
      slides.push(lenderPanelItems.slice(i, i + chunkSize));
    }
    return slides;
  }, [lenderPanelItems]);

  useEffect(() => {
    const syncViewportMode = () => {
      setIsMobileLenderCarousel(window.innerWidth < 768);
    };

    syncViewportMode();
    window.addEventListener('resize', syncViewportMode);

    return () => window.removeEventListener('resize', syncViewportMode);
  }, []);

  useEffect(() => {
    if (!isMobileLenderCarousel || mobileLenderSlides.length <= 1) return;

    const autoSlideTimer = window.setInterval(() => {
      setMobileLenderSlideIndex((prev) => (prev + 1) % mobileLenderSlides.length);
    }, 3200);

    return () => window.clearInterval(autoSlideTimer);
  }, [isMobileLenderCarousel, mobileLenderSlides.length]);

  useEffect(() => {
    if (!isMobileLenderCarousel) {
      setMobileLenderSlideIndex(0);
    }
  }, [isMobileLenderCarousel]);

  const goToPrevMobileLenderSlide = () => {
    setMobileLenderSlideIndex((prev) => (prev - 1 + mobileLenderSlides.length) % mobileLenderSlides.length);
  };

  const goToNextMobileLenderSlide = () => {
    setMobileLenderSlideIndex((prev) => (prev + 1) % mobileLenderSlides.length);
  };

  const handleMobileLenderTouchStart = (event) => {
    setMobileLenderTouchStartX(event.touches[0].clientX);
  };

  const handleMobileLenderTouchEnd = (event) => {
    if (mobileLenderTouchStartX === null) return;

    const touchEndX = event.changedTouches[0].clientX;
    const deltaX = mobileLenderTouchStartX - touchEndX;

    if (Math.abs(deltaX) > 40) {
      if (deltaX > 0) {
        goToNextMobileLenderSlide();
      } else {
        goToPrevMobileLenderSlide();
      }
    }

    setMobileLenderTouchStartX(null);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEOHead
        title="Finance Broker Australia | Business & Personal Loans | Flash Funding"
        description="Flash Funding matches Australian businesses and individuals with the right lender fast. Asset finance, business loans, car loans. 40+ lenders. No credit impact."
        keywords="car loans, personal loans, business finance, asset finance, cash flow finance, lines of credit, australia"
        canonical="/"
        schema={[HOME_FAQ_SCHEMA]}
      />

      <section className="relative overflow-hidden pb-10 pt-[88px] sm:pb-16 sm:pt-28 lg:pt-32" data-testid="home-hero-section">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-16 -top-20 h-72 w-72 rounded-full bg-purple-200/60 blur-3xl" />
          <div className="absolute right-0 top-16 h-72 w-72 rounded-full bg-purple-300/35 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 h-56 w-56 -translate-x-1/2 rounded-full bg-purple-100 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-7 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-12">
            <div className="min-w-0 space-y-4 sm:space-y-6 lg:space-y-7" data-testid="home-hero-left-panel">
              <div className="inline-flex w-full max-w-full items-center justify-center gap-2 rounded-full border border-purple-300 bg-purple-50/90 px-3 py-2 sm:w-auto sm:justify-start sm:px-4" data-testid="home-hero-badge">
                <ShieldCheck size={14} className="text-purple-700/90" />
                <span className="text-center text-[10px] font-semibold tracking-[0.06em] text-purple-700 sm:text-left sm:text-[11px] sm:tracking-[0.08em]">Flash Funding • Fast, Simple Finance Options</span>
              </div>

              <h1 className="max-w-3xl text-[2.05rem] font-bold leading-[0.98] tracking-[-0.018em] text-[#0F0A1A] sm:text-5xl lg:text-6xl" data-testid="home-hero-heading">
                Fast. Simple. Flash Funding.
              </h1>

              <p className="max-w-2xl text-[15px] leading-relaxed text-[#4A4556] sm:text-[17px]" data-testid="home-hero-subheading">
                <span className="sm:hidden">
                  Personal and business finance options with broker-guided support and access to 40+ lenders.
                </span>
                <span className="hidden sm:inline">
                  Personal and business finance options made easier. Whether you’re buying a car, funding equipment or growing your business, we help guide you through your options with confidence.
                </span>
              </p>

              <p className="hidden max-w-2xl text-sm leading-relaxed text-[#625c76] sm:block" data-testid="home-hero-supporting-line">
                Access 40+ lenders with a simple, broker-guided process designed to help you move faster with confidence.
              </p>

              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3" data-testid="home-hero-cta-row">
                <a href={PORTAL_LINKS.GENERIC} className="w-full sm:w-auto" data-testid="home-hero-primary-link">
                  <Button
                    data-testid="home-hero-primary-cta"
                    className="h-10 w-full rounded-lg bg-purple-600 px-4 text-[14px] font-semibold text-white transition-[transform,background-color] hover:-translate-y-[1px] hover:bg-purple-700 sm:h-11 sm:w-auto sm:px-5 sm:text-sm"
                  >
                    Check Your Options
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </a>
                <a
                  href="tel:+611800003527"
                  className="inline-flex h-10 w-full items-center justify-center rounded-lg border border-purple-300 bg-white px-4 text-[14px] font-semibold text-purple-700 transition-[background-color,color,border-color] hover:border-purple-500 hover:bg-purple-50 sm:h-11 sm:w-auto sm:px-5 sm:text-sm"
                  data-testid="home-hero-phone-link"
                >
                  Call 1800 003 527
                </a>
              </div>

              <div className="grid gap-2 sm:grid-cols-2 sm:gap-3" data-testid="home-hero-trust-points">
                <div className="rounded-xl border border-purple-100 bg-white px-3 py-2.5 sm:rounded-2xl sm:p-4" data-testid="home-hero-point-lenders">
                  <p className="text-[10px] uppercase tracking-[0.13em] text-[#7B758A] sm:text-xs sm:tracking-[0.14em]">Lender Panel</p>
                  <p className="mt-0.5 text-[15px] font-semibold leading-tight text-[#1F1735] sm:mt-1 sm:text-sm">40+ lenders</p>
                </div>
                <div className="rounded-xl border border-purple-100 bg-white px-3 py-2.5 sm:rounded-2xl sm:p-4" data-testid="home-hero-point-score">
                  <p className="text-[10px] uppercase tracking-[0.13em] text-[#7B758A] sm:text-xs sm:tracking-[0.14em]">Credit Score</p>
                  <p className="mt-0.5 text-[15px] font-semibold leading-tight text-[#1F1735] sm:mt-1 sm:text-sm">No impact to check options</p>
                </div>
              </div>

              <div className="rounded-2xl border border-purple-200 bg-white p-4" data-testid="home-hero-lender-strip">
                <p className="mb-3 text-xs uppercase tracking-[0.14em] text-[#7B758A]" data-testid="home-hero-lender-strip-label">
                  Accredited lender panel
                </p>
                <div className="md:hidden" data-testid="home-hero-lender-carousel-mobile">
                  <div
                    className="overflow-hidden rounded-xl"
                    onTouchStart={handleMobileLenderTouchStart}
                    onTouchEnd={handleMobileLenderTouchEnd}
                    data-testid="home-hero-lender-carousel-viewport"
                  >
                    <div
                      className="flex transition-transform duration-500 ease-out"
                      style={{ transform: `translateX(-${mobileLenderSlideIndex * 100}%)` }}
                      data-testid="home-hero-lender-carousel-track"
                    >
                      {mobileLenderSlides.map((slide, slideIndex) => (
                        <div key={`mobile-lender-slide-${slideIndex}`} className="w-full shrink-0" data-testid={`home-hero-lender-carousel-slide-${slideIndex}`}>
                          <div className="grid grid-cols-2 gap-2">
                            {slide.map((item) => (
                              <div
                                key={item.name}
                                className={`flex h-12 items-center justify-center rounded-xl px-2 ${
                                  item.type === 'placeholder'
                                    ? 'border border-dashed border-purple-200 bg-[#fcfbfe]'
                                    : 'border border-purple-100 bg-[#fcfbfe]'
                                }`}
                                data-testid={item.type === 'placeholder' ? 'home-hero-lender-logo-more-coming' : `home-hero-lender-logo-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                              >
                                {item.type === 'placeholder' ? (
                                  <span className="text-center text-[11px] font-semibold leading-tight text-[#716A85]">And Many More</span>
                                ) : (
                                  <img
                                    src={item.image}
                                    alt={`${item.name} logo`}
                                    className="max-h-6 w-auto object-contain"
                                    loading="lazy"
                                    data-testid={`home-hero-lender-logo-image-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-2 flex items-center justify-between" data-testid="home-hero-lender-carousel-controls">
                    <button
                      type="button"
                      onClick={goToPrevMobileLenderSlide}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-purple-200 bg-white text-purple-700"
                      data-testid="home-hero-lender-carousel-prev-button"
                      aria-label="Previous lender logos"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <div className="flex items-center gap-1.5" data-testid="home-hero-lender-carousel-dots">
                      {mobileLenderSlides.map((_, index) => (
                        <button
                          key={`lender-dot-${index}`}
                          type="button"
                          onClick={() => setMobileLenderSlideIndex(index)}
                          className={`h-2 rounded-full transition-all ${mobileLenderSlideIndex === index ? 'w-5 bg-purple-600' : 'w-2 bg-purple-200'}`}
                          data-testid={`home-hero-lender-carousel-dot-${index}`}
                          aria-label={`Go to lender slide ${index + 1}`}
                        />
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={goToNextMobileLenderSlide}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-purple-200 bg-white text-purple-700"
                      data-testid="home-hero-lender-carousel-next-button"
                      aria-label="Next lender logos"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="hidden md:grid md:grid-cols-4 md:gap-3 lg:grid-cols-6 xl:grid-cols-7" data-testid="home-hero-lender-logos-grid">
                  {lenderPanelItems.map((item) => (
                    <div
                      key={item.name}
                      className={`flex h-12 items-center justify-center rounded-xl px-2 ${
                        item.type === 'placeholder'
                          ? 'border border-dashed border-purple-200 bg-[#fcfbfe]'
                          : 'border border-purple-100 bg-[#fcfbfe]'
                      }`}
                      data-testid={item.type === 'placeholder' ? 'home-hero-lender-logo-more-coming' : `home-hero-lender-logo-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {item.type === 'placeholder' ? (
                        <span className="text-center text-[11px] font-semibold leading-tight text-[#716A85]">And Many More</span>
                      ) : (
                        <img
                          src={item.image}
                          alt={`${item.name} logo`}
                          className="max-h-6 w-auto object-contain"
                          loading="lazy"
                          data-testid={`home-hero-lender-logo-image-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <article className="w-full min-w-0 rounded-3xl border border-purple-200 bg-white p-6 shadow-xl shadow-purple-200/30 sm:p-8" data-testid="home-finance-finder-card">
              <p className="text-xs uppercase tracking-[0.16em] text-[#7B758A]" data-testid="finance-finder-step-label">
                Guided broker process • Step 1 of 4
              </p>
              <h2 className="mt-2 text-[30px] font-bold leading-tight text-[#0F0A1A]" data-testid="finance-finder-question-heading">
                Find Your Match in Minutes
              </h2>

              <p className="mt-2 text-sm text-[#5D5672]" data-testid="finance-finder-question-supporting-text">
                Select your finance type to start a quick, broker-guided pathway.
              </p>

              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="finance-finder-options-grid">
                {FINANCE_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  const isSelected = selectedPurpose === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setSelectedPurpose(option.key)}
                      className={`flex h-14 items-center justify-start rounded-xl border px-3 text-sm font-semibold transition-[transform,border-color,background-color,color] ${
                        isSelected
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-purple-100 bg-white text-[#463E5E] hover:-translate-y-[1px] hover:border-purple-300 hover:bg-purple-50'
                      }`}
                      data-testid={`finance-finder-option-${option.key}`}
                    >
                      <Icon size={16} className="mr-2" />
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 rounded-2xl border border-purple-100 bg-[#f9f7fd] p-4" data-testid="finance-finder-selection-summary">
                <p className="text-xs uppercase tracking-[0.14em] text-[#7B758A]">Selected finance type</p>
                <p className="mt-1 text-sm font-semibold text-[#1F1735]" data-testid="finance-finder-selected-value">
                  {selectedOption.label}
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-3" data-testid="finance-finder-cta-group">
                {selectedFinderCta.external ? (
                  <a href={selectedFinderCta.href} data-testid="finance-finder-primary-link">
                    <Button
                      data-testid="finance-finder-primary-cta"
                      className="h-11 w-full rounded-lg bg-purple-600 text-sm font-semibold text-white transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-purple-700"
                    >
                      {selectedFinderCta.label}
                      <ArrowRight size={15} className="ml-2" />
                    </Button>
                  </a>
                ) : (
                  <Link to={selectedFinderCta.href} data-testid="finance-finder-primary-link">
                    <Button
                      data-testid="finance-finder-primary-cta"
                      className="h-11 w-full rounded-lg bg-purple-600 text-sm font-semibold text-white transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-purple-700"
                    >
                      {selectedFinderCta.label}
                      <ArrowRight size={15} className="ml-2" />
                    </Button>
                  </Link>
                )}

                <Link
                  to={selectedOption.route}
                  className="inline-flex h-11 items-center justify-center rounded-lg border border-purple-300 bg-white px-4 text-sm font-semibold text-purple-700 transition-[background-color,border-color] hover:border-purple-500 hover:bg-purple-50"
                  data-testid="finance-finder-secondary-link"
                >
                  Explore {selectedOption.label}
                  <ChevronRight size={15} className="ml-1" />
                </Link>
              </div>

              <p className="mt-4 text-center text-xs text-[#6A637C]" data-testid="finance-finder-trust-line">
                A broker-guided start with no impact to check options.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="border-y border-purple-100 bg-white py-8" data-testid="home-trust-bar-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4" data-testid="home-trust-bar-content">
            <div className="inline-flex items-center rounded-full bg-purple-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-purple-700" data-testid="home-trust-badge">
              Trusted by Australians
            </div>

            <div className="flex items-center gap-2" data-testid="home-trust-stars-wrap">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={star} size={14} className="fill-[#f5b23f] text-[#f5b23f]" data-testid={`home-trust-star-${star}`} />
              ))}
              <span className="text-sm font-medium text-[#4A4556]" data-testid="home-trust-stars-label">5-star customer experience</span>
            </div>

            <div className="flex flex-wrap gap-2" data-testid="home-trust-lender-tags">
              {['40+ lender options', 'Broker-guided support', 'Fast enquiry process', 'Personal & business finance', 'More lender choice'].map((item) => (
                <span
                  key={item}
                  className="rounded-full border border-purple-200 bg-white px-3 py-1 text-xs text-[#5A5370]"
                  data-testid={`home-trust-tag-${item.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="home-pathway-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-3 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="home-pathway-heading">
            Choose your pathway
          </h2>
          <p className="mb-8 text-sm text-[#4A4556]" data-testid="home-pathway-subheading">
            Personal and business journeys with lender matching built in from the start.
          </p>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="home-pathway-grid">
            <Link
              to="/car-loans"
              className="group overflow-hidden rounded-3xl border border-purple-200 bg-white shadow-sm transition-[transform,box-shadow,border-color] hover:-translate-y-1 hover:border-purple-400 hover:shadow-xl hover:shadow-purple-200/40"
              data-testid="home-personal-path-card"
            >
              <div className="aspect-[16/10] overflow-hidden" data-testid="home-personal-path-image-wrap">
                <img
                  src={PATHWAY_IMAGES.personal}
                  alt="Personal finance pathway"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                  data-testid="home-personal-path-image"
                />
              </div>
              <div className="space-y-4 p-6">
                <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-purple-700" data-testid="home-personal-path-badge">
                  <UserRound size={14} className="mr-1.5" /> Personal Finance
                </div>
                <h3 className="text-2xl font-bold text-[#1F1735]" data-testid="home-personal-path-title">Car and personal lending made simple</h3>
                <p className="text-sm leading-relaxed text-[#4A4556]" data-testid="home-personal-path-description">
                  Fast, guided support for car and personal finance applications with clear next steps.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-purple-700" data-testid="home-personal-path-cta">
                  Explore Personal Finance <ArrowRight size={14} className="ml-1" />
                </span>
              </div>
            </Link>

            <Link
              to="/business-loans"
              className="group overflow-hidden rounded-3xl border border-purple-200 bg-white shadow-sm transition-[transform,box-shadow,border-color] hover:-translate-y-1 hover:border-purple-400 hover:shadow-xl hover:shadow-purple-200/40"
              data-testid="home-business-path-card"
            >
              <div className="aspect-[16/10] overflow-hidden" data-testid="home-business-path-image-wrap">
                <img
                  src={PATHWAY_IMAGES.business}
                  alt="Business and asset finance pathway"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                  loading="lazy"
                  data-testid="home-business-path-image"
                />
              </div>
              <div className="space-y-4 p-6">
                <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-purple-700" data-testid="home-business-path-badge">
                  <Building2 size={14} className="mr-1.5" /> Business & Asset
                </div>
                <h3 className="text-2xl font-bold text-[#1F1735]" data-testid="home-business-path-title">Funding that supports business growth</h3>
                <p className="text-sm leading-relaxed text-[#4A4556]" data-testid="home-business-path-description">
                  Asset finance, equipment lending, and commercial loans matched to your profile.
                </p>
                <span className="inline-flex items-center text-sm font-semibold text-purple-700" data-testid="home-business-path-cta">
                  Explore Business Finance <ArrowRight size={14} className="ml-1" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16" data-testid="home-how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="home-how-it-works-heading">
            How It Works
          </h2>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3" data-testid="home-how-it-works-grid">
            {HOW_IT_WORKS.map((step, index) => (
              <article key={step.title} className="rounded-2xl border border-purple-200 bg-[#faf9fd] p-6" data-testid={`home-how-step-${index + 1}`}>
                <span className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 text-sm font-semibold text-purple-700" data-testid={`home-how-step-number-${index + 1}`}>
                  {index + 1}
                </span>
                <h3 className="text-lg font-semibold text-[#1F1735]" data-testid={`home-how-step-title-${index + 1}`}>{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#4A4556]" data-testid={`home-how-step-text-${index + 1}`}>{step.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="home-calculator-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-purple-200 bg-white p-6 sm:p-8 lg:p-10" data-testid="home-calculator-card">
            <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
              <div>
                <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-purple-700" data-testid="home-calculator-badge">
                  <SlidersHorizontal size={13} className="mr-1.5" /> Calculator preview
                </div>
                <h2 className="mt-3 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="home-calculator-heading">
                  See what your repayments could look like.
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#4A4556]" data-testid="home-calculator-subheading">
                  Move the sliders to explore a simple repayment estimate before diving into the full calculator suite.
                </p>
                <Link to="/calculators" data-testid="home-calculator-link">
                  <Button
                    variant="outline"
                    data-testid="home-calculator-cta"
                    className="mt-5 h-11 rounded-full border-purple-300 bg-purple-50 px-5 text-xs font-bold uppercase tracking-[0.14em] text-purple-700 hover:bg-purple-100"
                  >
                    Open full calculators
                  </Button>
                </Link>
              </div>

              <div className="rounded-2xl border border-purple-100 bg-[#f8f5fc] p-5 sm:p-6" data-testid="home-calculator-controls">
                <div className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between" data-testid="home-calculator-amount-row">
                      <label className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" htmlFor="repayment-amount" data-testid="home-calculator-amount-label">Amount</label>
                      <span className="text-sm font-semibold text-[#1F1735]" data-testid="home-calculator-amount-value">
                        ${amount.toLocaleString('en-AU')}
                      </span>
                    </div>
                    <input
                      id="repayment-amount"
                      type="range"
                      min={10000}
                      max={400000}
                      step={5000}
                      value={amount}
                      onChange={(event) => setAmount(Number(event.target.value))}
                      className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-purple-200"
                      data-testid="home-calculator-amount-slider"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between" data-testid="home-calculator-term-row">
                      <label className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" htmlFor="repayment-term" data-testid="home-calculator-term-label">Term</label>
                      <span className="text-sm font-semibold text-[#1F1735]" data-testid="home-calculator-term-value">
                        {termYears} years
                      </span>
                    </div>
                    <input
                      id="repayment-term"
                      type="range"
                      min={1}
                      max={7}
                      step={1}
                      value={termYears}
                      onChange={(event) => setTermYears(Number(event.target.value))}
                      className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-purple-200"
                      data-testid="home-calculator-term-slider"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between" data-testid="home-calculator-rate-row">
                      <label className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" htmlFor="repayment-rate" data-testid="home-calculator-rate-label">Interest rate</label>
                      <span className="text-sm font-semibold text-[#1F1735]" data-testid="home-calculator-rate-value">
                        {interestRate.toFixed(1)}%
                      </span>
                    </div>
                    <input
                      id="repayment-rate"
                      type="range"
                      min={5}
                      max={18}
                      step={0.1}
                      value={interestRate}
                      onChange={(event) => setInterestRate(Number(event.target.value))}
                      className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-purple-200"
                      data-testid="home-calculator-rate-slider"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between" data-testid="home-calculator-balloon-row">
                      <label className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" htmlFor="repayment-balloon" data-testid="home-calculator-balloon-label">Balloon</label>
                      <span className="text-sm font-semibold text-[#1F1735]" data-testid="home-calculator-balloon-value">
                        {balloonPercent}%
                      </span>
                    </div>
                    <input
                      id="repayment-balloon"
                      type="range"
                      min={0}
                      max={40}
                      step={1}
                      value={balloonPercent}
                      onChange={(event) => setBalloonPercent(Number(event.target.value))}
                      className="mt-2 h-2 w-full cursor-pointer appearance-none rounded-full bg-purple-200"
                      data-testid="home-calculator-balloon-slider"
                    />
                  </div>

                  <div className="rounded-2xl bg-white p-4" data-testid="home-calculator-estimate-card">
                    <p className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" data-testid="home-calculator-estimate-label">Estimated monthly repayment</p>
                    <p className="mt-1 text-2xl font-bold text-[#1F1735]" data-testid="home-calculator-estimate-value">
                      ${monthlyRepayment.toLocaleString('en-AU', { maximumFractionDigits: 0 })}
                    </p>
                    <p className="mt-1 text-xs text-[#7B758A]" data-testid="home-calculator-estimate-note">
                      Indicative only. Includes {interestRate.toFixed(1)}% rate and {balloonPercent}% balloon assumption.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GoogleReviews variant="home" />

      <section className="py-16" data-testid="home-educational-hub-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="mb-3 text-3xl font-bold text-[#0F0A1A] sm:text-4xl" data-testid="home-educational-hub-heading">
            Learn before you apply
          </h2>
          <p className="mb-8 text-sm text-[#4A4556]" data-testid="home-educational-hub-subheading">
            Borrowing guides and lender insights designed to help you make confident decisions.
          </p>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="home-educational-hub-grid">
            {GUIDE_CARDS.map((guide) => (
              <Link
                to={guide.path}
                key={guide.path}
                className="rounded-2xl border border-purple-200 bg-white p-5 transition-[transform,border-color,box-shadow] hover:-translate-y-1 hover:border-purple-400 hover:shadow-lg hover:shadow-purple-200/30"
                data-testid={`home-educational-card-${guide.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                <h3 className="text-lg font-semibold text-[#1F1735]" data-testid={`home-educational-title-${guide.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>{guide.title}</h3>
                <p className="mt-2 text-sm text-[#4A4556]" data-testid={`home-educational-description-${guide.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  {guide.description}
                </p>
                <span className="mt-4 inline-flex items-center text-sm font-semibold text-purple-700" data-testid={`home-educational-link-${guide.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  Read now <ArrowRight size={14} className="ml-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 lg:py-24" data-testid="home-final-cta-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-purple-700 px-6 py-10 text-center sm:px-10 sm:py-12" data-testid="home-final-cta-card">
            <h2 className="font-heading text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="home-final-cta-heading">
              Ready to see what you can get approved for?
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-white/85" data-testid="home-final-cta-subheading">
              Start your guided pathway and we will handle the lender matching and next-step strategy.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row" data-testid="home-final-cta-buttons">
              <a href={PORTAL_LINKS.GENERIC} data-testid="home-final-check-options-link">
                <Button
                  data-testid="home-final-check-options-cta"
                  className="h-12 w-full rounded-full bg-white px-8 text-xs font-heading font-bold uppercase tracking-[0.14em] text-purple-700 transition-[background-color,transform] hover:-translate-y-[1px] hover:bg-purple-50 sm:w-auto"
                >
                  Check Your Options
                </Button>
              </a>
              <a href={PORTAL_LINKS.GENERIC} data-testid="home-final-check-options-secondary-link">
                <Button
                  variant="outline"
                  data-testid="home-final-check-options-secondary-cta"
                  className="h-12 w-full rounded-full border-white/70 bg-transparent px-8 text-xs font-heading font-bold uppercase tracking-[0.14em] text-white transition-[background-color,border-color] hover:border-white hover:bg-white/10 sm:w-auto"
                >
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-14" data-testid="home-compliance-strip-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-purple-200 bg-white px-5 py-4" data-testid="home-compliance-strip-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs uppercase tracking-[0.14em] text-[#7B758A]" data-testid="home-compliance-strip-label">
                Trust and compliance
              </p>
              <div className="inline-flex items-center gap-2 text-sm text-[#4A4556]" data-testid="home-compliance-strip-content">
                <Clock3 size={16} className="text-purple-700" />
                Australian Credit Licence framework • Broker-guided process
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
